import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useDatabaseContext } from '../../context'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { LoaderCircle, Plus } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { TooltipPortal } from '@radix-ui/react-tooltip'

interface IImportCsvProps {
  onImportEnd: (headers: string[], data: any[]) => void
}

export const ImportCsv = ({ onImportEnd }: IImportCsvProps) => {
  const t = useTranslations('database')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isImporting, setIsImporting] = useState(false)

  const { project, tableName } = useDatabaseContext()

  const handleFileImport = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file || !project || !tableName) return

    // Check if file is CSV or Excel
    const fileType = file.name.split('.').pop()?.toLowerCase()
    if (fileType !== 'csv' && fileType !== 'xlsx' && fileType !== 'xls') {
      toast.error(t('file_format_invalid'))
      return
    }

    try {
      setIsImporting(true)

      const reader = new FileReader()

      reader.onload = (e: any) => {
        const content = e.target.result
        const lines = content
          .split(/\r?\n/)
          .filter((line: any) => line.trim() !== '')

        if (lines.length === 0) {
          console.error('CSV file is empty')
          return
        }

        const parseCsvLine = (line: string) => {
          const regex = /(?:"([^"]*(?:""[^"]*)*)"|([^,]*))(,|$)/g
          const values: string[] = []
          for (;;) {
            const match = regex.exec(line)
            if (match === null) {
              break
            }
            const value =
              match[1] !== undefined ? match[1].replace(/""/g, '"') : match[2]
            values.push(value)
            if (match[3] === '') {
              // This was the last column in the row
              break
            }
          }
          if (line.endsWith(',')) {
            values.push('')
          }
          return values
        }

        // 解析表头（第一行）
        const headers = parseCsvLine(lines[0])

        // 解析数据（剩余行）
        const data = lines.slice(1).map((line: any) => {
          const values = parseCsvLine(line)
          return headers.reduce((obj: any, header: any, index: any) => {
            obj[header] = values[index] || '' // 处理可能缺失的值
            return obj
          }, {})
        })

        onImportEnd?.(headers, data)
      }

      reader.onerror = () => {
        toast.error(t('failed_to_import_file'))
      }

      reader.readAsText(file, 'UTF-8')
    } catch (error) {
      console.error('Import failed:', error)
      toast.error(
        error instanceof Error ? error.message : t('failed_to_import_file'),
      )
    } finally {
      setIsImporting(false)
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileImport}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              disabled={isImporting}
              onClick={() => fileInputRef.current?.click()}
              className="w-[220px] bg-white/5 outline outline-1 outline-offset-[-1px] outline-[#fd12e4]  rounded-full bg-[#241E33] hover:bg-[#241E33]/80 h-[40px]"
            >
              {isImporting ? (
                <LoaderCircle className="h-4 w-4 animate-spin text-[#FD24E4]" />
              ) : (
                <Plus className="h-4 w-4 text-[#FD24E4]" />
              )}
              <div className="bg-gradient-to-r from-[#FD12E4] to-[#1BECFD] bg-clip-text text-transparent">
                {isImporting ? t('importing') : t('import_csv_excel')}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="TooltipContent" sideOffset={5}>
              {t('import_csv_excel_tooltip')}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    </>
  )
}
